import React, { createContext, useContext, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import useAuthStore from '../store/authStore';
import { ORDER_KEYS } from '../hooks/useOrders';

const SOCKET_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';

const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const socketRef  = useRef(null);
  const { user, isAuthenticated } = useAuthStore();
  const qc = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    socketRef.current = io(SOCKET_URL, { withCredentials: true, transports: ['websocket'] });
    socketRef.current.emit('join', user._id || user.id);

    // Live order status updates
    socketRef.current.on('order:statusUpdated', (data) => {
      qc.invalidateQueries({ queryKey: ORDER_KEYS.all });
      qc.invalidateQueries({ queryKey: ORDER_KEYS.detail(data.orderId) });
      qc.invalidateQueries({ queryKey: ORDER_KEYS.stats });

      const statusEmoji = {
        confirmed:  '✅', processing: '⚙️', shipped: '🚚',
        delivered:  '📦', cancelled:  '❌',
      };
      toast.info(
        `${statusEmoji[data.status] || '📋'} Order ${data.orderNumber} is now ${data.status}`,
        { autoClose: 5000 }
      );
    });

    // Live payment updates
    socketRef.current.on('payment:updated', (data) => {
      qc.invalidateQueries({ queryKey: ['payment-status', data.orderId] });
      qc.invalidateQueries({ queryKey: ORDER_KEYS.all });
      if (data.paymentStatus === 'paid') {
        toast.success('Payment confirmed! Your order is being processed.', { autoClose: 5000 });
      } else if (data.paymentStatus === 'failed') {
        toast.error('Payment failed. Please try again.', { autoClose: 5000 });
      }
    });

    return () => socketRef.current?.disconnect();
  }, [isAuthenticated, user]);

  return (
    <SocketContext.Provider value={socketRef.current}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocketContext = () => useContext(SocketContext);
